import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Space,
  Statistic,
  Table,
  Progress,
  Alert,
  Button,
  Grid,
  Tabs,
  Tag,
  Empty,
  Divider,
} from 'antd';
import type { Dayjs } from 'dayjs';
import {
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BankOutlined,
  TrophyOutlined,
  GoldOutlined,
  CrownOutlined,
  WalletOutlined,
  ThunderboltOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { ExportButton } from '@/components/ExportButton';
import { exportOptions } from '@/constants/exportOptions';
import { AutoRefreshToggle, AnalyticsDateRangeFilter } from '@/components';
import type { AnalyticsDateRange, AnalyticsPeriod, AnalyticsShownRange } from '@/components';
import { useAutoRefresh } from '@hooks/useAutoRefresh';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { MatchBreakdownPanel } from './MatchBreakdownPanel';
import { ReportGroupPanels } from './ReportGroupPanels';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface DivisionDistribution {
  divisionId: number;
  divisionName: string;
  tierName: string;
  playerCount: number;
  totalMatches: number;
  winPercentage: number;
  percentage: number;
}

interface CurrencyFlowSummary {
  sources: Array<{ source: string; amount: number; percentage: number }>;
  sinks: Array<{ source: string; amount: number; percentage: number }>;
  inflow: number;
  outflow: number;
  netFlow: number;
}

type CurrencyKey = 'coins' | 'evoTokens' | 'matchTokens' | 'premiumCurrency';

interface EconomyData {
  totalCoinsInCirculation: number;
  totalGemsInCirculation: number;
  totalEvoTokensInCirculation: number;
  totalMatchTokensInCirculation: number;
  avgCoinsPerUser: number;
  avgGemsPerUser: number;
  avgEvoTokensPerUser: number;
  avgMatchTokensPerUser: number;
  coinsInflowToday: number;
  coinsOutflowToday: number;
  netCoinFlow: number;
  /** @deprecated use flows.coins.sources */
  coinSources: Array<{ source: string; amount: number; percentage: number }>;
  /** @deprecated use flows.coins.sinks */
  coinSinks: Array<{ source: string; amount: number; percentage: number }>;
  flows?: Record<CurrencyKey, CurrencyFlowSummary>;
}

interface EconomyPerDivision {
  divisionId: number;
  divisionName: string;
  tierName: string;
  playerCount: number;
  avgCoins: number;
  avgEvoTokens: number;
  avgMatchTokens: number;
  avgGems: number;
  avgLevel: number;
  avgFanRating: number;
  totalCoins: number;
}

export const AnalyticsPage: React.FC = () => {
  const screens = useBreakpoint();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('last_7_days');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [divisionDistribution, setDivisionDistribution] = useState<DivisionDistribution[]>([]);
  const [economy, setEconomy] = useState<EconomyData | null>(null);
  const [economyPerDivision, setEconomyPerDivision] = useState<EconomyPerDivision[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30_000);
  // The range and fetch time of the figures on screen, for the date label.
  const [shown, setShown] = useState<AnalyticsShownRange | null>(null);
  const beginAnalytics = useLatestRequest();

  const fetchAnalytics = React.useCallback(async () => {
    const isLatest = beginAnalytics();
    setLoading(true);
    setError(null);
    const startedAt = Date.now();
    const range: AnalyticsDateRange = {
      period: period as AnalyticsPeriod,
      customRange: customRange ?? undefined,
    };
    try {
      const params: Record<string, string> = {};
      if (period === 'custom' && customRange) {
        params.period = 'custom';
        params.startDate = customRange[0].format('YYYY-MM-DD');
        params.endDate = customRange[1].format('YYYY-MM-DD');
      } else {
        params.period = period;
      }
      // Risk & Hack Detection moved to the Risks & Hacks page (27 Oct 2026).
      const [divisionRes, economyRes, ecoPerDivRes] = await Promise.all([
        adminApi.getDivisionDistribution(),
        adminApi.getEconomyAnalytics(params),
        adminApi.getEconomyPerDivision(),
      ]);
      if (!isLatest()) return;
      setDivisionDistribution(divisionRes.data || []);
      setEconomy(economyRes.data || null);
      setEconomyPerDivision(ecoPerDivRes.data || []);
      setShown({ range, asOf: startedAt });
    } catch (err) {
      if (!isLatest()) return;
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data. Please check your connection and try again.');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [period, customRange, beginAnalytics]);

  useEffect(() => {
    fetchAnalytics();
  }, [period, fetchAnalytics]);

  useAutoRefresh(fetchAnalytics, autoRefresh, refreshInterval);

  const divisionColumns = [
    { title: 'Division', dataIndex: 'divisionName', key: 'divisionName' },
    { title: 'Tier', dataIndex: 'tierName', key: 'tierName' },
    {
      title: 'Players',
      dataIndex: 'playerCount',
      key: 'playerCount',
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: 'Matches',
      dataIndex: 'totalMatches',
      key: 'totalMatches',
      render: (count: number) => (count ?? 0).toLocaleString(),
    },
    {
      title: 'Win %',
      dataIndex: 'winPercentage',
      key: 'winPercentage',
      render: (pct: number) => {
        const safe = Number.isFinite(pct) ? pct : 0;
        const color = safe >= 55 ? '#52c41a' : safe >= 45 ? '#1890ff' : '#faad14';
        return <Text style={{ color, fontWeight: 600 }}>{safe.toFixed(2)}%</Text>;
      },
    },
    {
      title: 'Distribution',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (pct: number) => (
        <Space>
          <Progress percent={pct} size="small" style={{ width: 100 }} />
          <Text>{pct}%</Text>
        </Space>
      ),
    },
  ];

  const economyPerDivColumns = [
    { title: 'Division', dataIndex: 'divisionName', key: 'divisionName', width: 160 },
    {
      title: 'Players',
      dataIndex: 'playerCount',
      key: 'playerCount',
      width: 90,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: 'Avg Coins',
      dataIndex: 'avgCoins',
      key: 'avgCoins',
      width: 110,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: 'Avg Evo Tokens',
      dataIndex: 'avgEvoTokens',
      key: 'avgEvoTokens',
      width: 120,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: 'Avg Match Tokens',
      dataIndex: 'avgMatchTokens',
      key: 'avgMatchTokens',
      width: 130,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: 'Avg Premium',
      dataIndex: 'avgGems',
      key: 'avgGems',
      width: 110,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: 'Avg Level',
      dataIndex: 'avgLevel',
      key: 'avgLevel',
      width: 90,
    },
    {
      title: 'Avg FR',
      dataIndex: 'avgFanRating',
      key: 'avgFanRating',
      width: 80,
      render: (v: number) => v.toLocaleString(),
    },
  ];

  if (loading && !economy) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: screens.xs ? 50 : 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error Loading Analytics"
          description={error}
          type="error"
          showIcon
          action={
            <Button icon={<ReloadOutlined />} onClick={fetchAnalytics}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Analytics
            </Title>
            <Text type="secondary">Division distribution and economy health</Text>
          </div>
          <Space>
            <ExportButton
              options={[
                exportOptions.revenue,
                exportOptions.inGameCurrencyBurn,
                exportOptions.dau,
                exportOptions.economyPerDivision,
                exportOptions.matchBreakdown,
                exportOptions.retentionFunnel,
                exportOptions.funnelDropOff,
              ]}
              buttonText="Export"
            />
            <AnalyticsDateRangeFilter
              value={{
                period: period as AnalyticsPeriod,
                customRange: customRange ?? undefined,
              }}
              loading={loading}
              shown={shown}
              onApply={(next: AnalyticsDateRange) => {
                setPeriod(next.period);
                setCustomRange(next.customRange ?? null);
              }}
            />

            <AutoRefreshToggle
              enabled={autoRefresh}
              onEnabledChange={setAutoRefresh}
              intervalMs={refreshInterval}
              onIntervalChange={setRefreshInterval}
            />
          </Space>
        </Space>
      </div>

      {/* Division Distribution */}
      <Card
        title={
          <Space>
            <TrophyOutlined style={{ color: '#1890ff' }} />
            <span>Division Distribution</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
        styles={{ body: { padding: screens.xs ? 8 : 24 } }}
      >
        <Table
          dataSource={divisionDistribution}
          columns={divisionColumns}
          rowKey="divisionId"
          pagination={false}
          size="small"
          scroll={{ x: 500 }}
        />
      </Card>

      {/* Economy Per Division */}
      <Card
        title={
          <Space>
            <PieChartOutlined style={{ color: '#1890ff' }} />
            <span>Economy Per Division</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
        styles={{ body: { padding: screens.xs ? 8 : 24 } }}
      >
        <Table
          dataSource={economyPerDivision}
          columns={economyPerDivColumns}
          rowKey="divisionId"
          pagination={false}
          size="small"
          scroll={{ x: 900 }}
        />
      </Card>

      {/* Economy Health */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BankOutlined style={{ color: '#1890ff' }} />
                <span>Economy Overview</span>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              {[
                {
                  label: 'Coins',
                  total: economy?.totalCoinsInCirculation || 0,
                  avg: economy?.avgCoinsPerUser || 0,
                  icon: <GoldOutlined style={{ color: '#faad14' }} />,
                },
                {
                  label: 'Premium',
                  total: economy?.totalGemsInCirculation || 0,
                  avg: economy?.avgGemsPerUser || 0,
                  icon: <CrownOutlined style={{ color: '#eb2f96' }} />,
                },
                {
                  label: 'Evo Tokens',
                  total: economy?.totalEvoTokensInCirculation || 0,
                  avg: economy?.avgEvoTokensPerUser || 0,
                  icon: <WalletOutlined style={{ color: '#13c2c2' }} />,
                },
                {
                  label: 'Match Tokens',
                  total: economy?.totalMatchTokensInCirculation || 0,
                  avg: economy?.avgMatchTokensPerUser || 0,
                  icon: <ThunderboltOutlined style={{ color: '#722ed1' }} />,
                },
              ].map((c) => (
                <Col key={c.label} span={12}>
                  <Card size="small" hoverable>
                    <Statistic
                      title={c.label}
                      value={c.total}
                      prefix={c.icon}
                      valueStyle={{ fontSize: 18 }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Avg: {c.avg.toLocaleString()} /user
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
            <Divider style={{ margin: '20px 0 12px' }} />
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
              FLOW SUMMARY
            </Text>
            <div style={{ marginTop: 8 }}>
              {(
                [
                  { key: 'coins', label: 'Coins' },
                  { key: 'matchTokens', label: 'Match Tokens' },
                  { key: 'evoTokens', label: 'Evo Tokens' },
                  { key: 'premiumCurrency', label: 'Premium' },
                ] as Array<{ key: CurrencyKey; label: string }>
              ).map(({ key, label }) => {
                const summary: CurrencyFlowSummary | undefined =
                  economy?.flows?.[key] ??
                  (key === 'coins' && economy
                    ? {
                        sources: [],
                        sinks: [],
                        inflow: economy.coinsInflowToday || 0,
                        outflow: economy.coinsOutflowToday || 0,
                        netFlow: economy.netCoinFlow || 0,
                      }
                    : undefined);
                const inflow = summary?.inflow ?? 0;
                const outflow = summary?.outflow ?? 0;
                const net = summary?.netFlow ?? 0;
                const hasData = inflow > 0 || outflow > 0;
                return (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: '1px dashed #f0f0f0',
                    }}
                  >
                    <Text strong style={{ fontSize: 13 }}>
                      {label}
                    </Text>
                    {hasData ? (
                      <Space size={10}>
                        <Text style={{ color: '#52c41a', fontSize: 13 }}>
                          <ArrowUpOutlined /> {inflow.toLocaleString()}
                        </Text>
                        <Text style={{ color: '#ff4d4f', fontSize: 13 }}>
                          <ArrowDownOutlined /> {outflow.toLocaleString()}
                        </Text>
                        <Tag
                          color={net >= 0 ? 'orange' : 'green'}
                          style={{ fontSize: 11, margin: 0 }}
                        >
                          {net >= 0 ? 'Inflation' : 'Deflation'} {Math.abs(net).toLocaleString()}
                        </Tag>
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        No flow in period
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BankOutlined style={{ color: '#1890ff' }} />
                <span>Currency Flow (Sources &amp; Sinks)</span>
              </Space>
            }
          >
            <Tabs
              defaultActiveKey="coins"
              items={(
                [
                  { key: 'coins', label: 'Coins' },
                  { key: 'matchTokens', label: 'Match Tokens' },
                  { key: 'evoTokens', label: 'Evo Tokens' },
                  { key: 'premiumCurrency', label: 'Premium' },
                ] as Array<{ key: CurrencyKey; label: string }>
              ).map(({ key, label }) => {
                // Backwards compatible: fall back to legacy coinSources/coinSinks
                // for the 'coins' tab if the backend hasn't deployed flows yet.
                const summary: CurrencyFlowSummary | undefined =
                  economy?.flows?.[key] ??
                  (key === 'coins' && economy
                    ? {
                        sources: economy.coinSources || [],
                        sinks: economy.coinSinks || [],
                        inflow: economy.coinsInflowToday || 0,
                        outflow: economy.coinsOutflowToday || 0,
                        netFlow: economy.netCoinFlow || 0,
                      }
                    : undefined);
                return {
                  key,
                  label,
                  children: (
                    <>
                      {summary && (
                        <Row gutter={16} style={{ marginBottom: 12 }}>
                          <Col span={8}>
                            <Statistic
                              title="Inflow"
                              value={summary.inflow}
                              valueStyle={{ color: '#52c41a', fontSize: 16 }}
                            />
                          </Col>
                          <Col span={8}>
                            <Statistic
                              title="Outflow"
                              value={summary.outflow}
                              valueStyle={{ color: '#ff4d4f', fontSize: 16 }}
                            />
                          </Col>
                          <Col span={8}>
                            <Statistic
                              title="Net"
                              value={summary.netFlow}
                              valueStyle={{
                                color: summary.netFlow >= 0 ? '#52c41a' : '#ff4d4f',
                                fontSize: 16,
                              }}
                            />
                          </Col>
                        </Row>
                      )}
                      <Row gutter={16}>
                        <Col span={12}>
                          <Space size={6} style={{ marginBottom: 12 }}>
                            <ArrowUpOutlined style={{ color: '#52c41a' }} />
                            <Text strong style={{ color: '#52c41a' }}>
                              Sources (Inflow)
                            </Text>
                          </Space>
                          {summary?.sources?.length ? (
                            summary.sources.map((source, i) => (
                              <div key={i} style={{ marginBottom: 10 }}>
                                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                  <Text style={{ fontSize: 13 }}>{source.source}</Text>
                                  <Text strong style={{ fontSize: 13 }}>
                                    {source.amount.toLocaleString()} ({source.percentage}%)
                                  </Text>
                                </Space>
                                <Progress
                                  percent={source.percentage}
                                  showInfo={false}
                                  size="small"
                                  strokeColor="#52c41a"
                                />
                              </div>
                            ))
                          ) : (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="No sources in period"
                              styles={{ image: { height: 40 } }}
                            />
                          )}
                        </Col>
                        <Col span={12}>
                          <Space size={6} style={{ marginBottom: 12 }}>
                            <ArrowDownOutlined style={{ color: '#ff4d4f' }} />
                            <Text strong style={{ color: '#ff4d4f' }}>
                              Sinks (Outflow)
                            </Text>
                          </Space>
                          {summary?.sinks?.length ? (
                            summary.sinks.map((sink, i) => (
                              <div key={i} style={{ marginBottom: 10 }}>
                                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                  <Text style={{ fontSize: 13 }}>{sink.source}</Text>
                                  <Text strong style={{ fontSize: 13 }}>
                                    {sink.amount.toLocaleString()} ({sink.percentage}%)
                                  </Text>
                                </Space>
                                <Progress
                                  percent={sink.percentage}
                                  showInfo={false}
                                  size="small"
                                  status="exception"
                                />
                              </div>
                            ))
                          ) : (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="No sinks in period"
                              styles={{ image: { height: 40 } }}
                            />
                          )}
                        </Col>
                      </Row>
                    </>
                  ),
                };
              })}
            />
          </Card>
        </Col>
      </Row>

      <MatchBreakdownPanel
        period={period as AnalyticsPeriod}
        startDate={period === 'custom' ? customRange?.[0].format('YYYY-MM-DD') : undefined}
        endDate={period === 'custom' ? customRange?.[1].format('YYYY-MM-DD') : undefined}
      />

      <ReportGroupPanels />
    </div>
  );
};

export default AnalyticsPage;
